/**
 * Inquiries needing action — Figma `Widget/InquiriesNeedingAction` (node 12:3101).
 *
 * Measured: the Card shell; a header row of `Table/HeaderCell` at h-32 with
 * `TableHeader/11` uppercase `text/muted` labels — CUSTOMER (168) · STAGE (160) ·
 * DAYS (62) · OWNER (104) · NEXT STEP (106); then h-40 rows with a bottom
 * `border/default` (the last row has none). Customer is `Body/13` medium
 * `text/primary`; owner is a 24px avatar plus `Body/13`; NEXT STEP is `Body/13`
 * `text/secondary`.
 *
 * ===========================================================================
 * "NEEDING ACTION" HAS NO ENDPOINT, SO IT IS DEFINED HERE — EXPLICITLY
 * ===========================================================================
 * Part A ships no "needs attention" query. What the design shows is unmistakable
 * though: the four rows carry New Inquiry / Need Quote / Pending Confirmation /
 * Advance Pending — every OPEN stage, never Lost. So the widget asks for the open
 * stages and orders them by the one signal the API does give: `stage_entered_at`,
 * oldest first, which is exactly the "12d" row the design puts an overdue tint on.
 *
 * TWO HONEST LIMITS, BOTH VISIBLE IN THE CODE RATHER THAN PAPERED OVER:
 *  1. `GET /countries/:cc/inquiries` exposes NO `sort` parameter (the controller
 *     reads `stage`, `assigned`, `q`, `page`, `perPage` and nothing else). The
 *     ordering is therefore done client-side over ONE page. With `perPage` at the
 *     API's maximum that page is the first 200 open inquiries, so on a pipeline
 *     larger than that the "oldest five" are the oldest five OF THAT PAGE. The
 *     widget's job — surface something stale enough to act on — survives that; a
 *     comment is the honest place to record it until a `sort` parameter exists.
 *  2. The design's NEXT STEP column is a per-stage phrase, not a stored field.
 *     Those phrases live in the ONE stage map (`domain/stages.ts:nextStep`), taken
 *     verbatim from this node, and are `null` for the stages the design does not
 *     show — where the cell renders an em dash rather than an invented instruction.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchInquiries, fetchStageCounts, inquiryKeys } from '../../lib/inquiries-api';
import type { InquiryDto } from '../../lib/inquiries-api';
import { OPEN_STAGES, STAGES, daysInStage } from '../../domain/stages';
import { Card, DaysPill, EmptyState, SkeletonRows, StagePill } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';

const VISIBLE_ROWS = 5;
/** The API's `MAX_PER_PAGE` (`support/http.ts`). Asking for more is silently clamped. */
const SCAN_PAGE = 200;

const LIST_PARAMS = {
  stage: OPEN_STAGES.join(','),
  perPage: SCAN_PAGE,
} as const;

export function InquiriesNeedingActionWidget({
  countryCode,
  canView,
}: {
  countryCode: string;
  canView: boolean;
}): React.JSX.Element | null {
  // A user without `inquiries.view` would get a 403 for this query. The widget is
  // not rendered at all rather than rendered-then-failed: the nav already hides
  // Inquiries for them, and a dashboard panel that exists only to show an error is
  // worse than a dashboard without the panel.
  const query = useQuery({
    queryKey: inquiryKeys.list(countryCode, LIST_PARAMS),
    queryFn: ({ signal }) => fetchInquiries(countryCode, LIST_PARAMS, signal),
    enabled: canView,
  });

  /**
   * HOW MANY INQUIRIES EXIST AT ALL — the difference between "you are up to date" and
   * "you have not started".
   *
   * ⚠ FOUND BY OPENING A BRAND-NEW PRODUCTION DATABASE (P4). The query above filters to
   * OPEN stages, so zero rows meant BOTH "every open inquiry has been dealt with" AND
   * "this country has never had an inquiry" — and the empty state said the first one.
   * Day one, the very first thing the owner reads on the very first screen, was the
   * system congratulating him for clearing a queue that had never contained anything,
   * and telling him nothing about what to do next.
   *
   * SAME QUERY KEY as `StageCountCards`, deliberately: react-query dedupes it, so this
   * costs no extra request. A second endpoint, or the same one under a different key,
   * would have added a round trip to every dashboard load to answer a question the page
   * was already asking one panel to the left.
   */
  const totals = useQuery({
    queryKey: inquiryKeys.stageCounts(countryCode),
    queryFn: ({ signal }) => fetchStageCounts(countryCode, signal),
    enabled: canView,
  });

  const rows = useMemo<InquiryDto[]>(() => {
    const data = query.data?.data ?? [];
    return [...data]
      .sort((a, b) => {
        // Oldest in stage first. A row with no `stage_entered_at` sorts last: it
        // carries no ageing signal, so it cannot claim to be the most urgent.
        const left = a.stage_entered_at;
        const right = b.stage_entered_at;
        if (left === null && right === null) return 0;
        if (left === null) return 1;
        if (right === null) return -1;
        return left.localeCompare(right);
      })
      .slice(0, VISIBLE_ROWS);
  }, [query.data]);

  if (!canView) return null;

  return (
    <Card
      title="Inquiries needing action"
      action={
        <Link
          to={`/countries/${countryCode}/inquiries`}
          className="rounded-sm px-2 py-0.5 text-label font-medium text-indigo hover:bg-indigo-soft"
        >
          View all
        </Link>
      }
      className="min-h-[388px]"
    >
      {query.isPending ? (
        <SkeletonRows rows={VISIBLE_ROWS} label="Loading inquiries needing action" />
      ) : query.isError ? (
        <EmptyState
          title="Inquiries could not be loaded"
          description="The pipeline did not answer. Try again in a moment."
        />
      ) : rows.length === 0 ? (
        /*
         * TWO DIFFERENT NOTHINGS. See the `totals` query above for how this was found.
         *
         * While the count is still loading the copy stays on the NEVER-STARTED wording,
         * not the caught-up one. Both are a guess for that instant, and guessing "you
         * have not started" at worst under-praises somebody; guessing "all dealt with"
         * at worst tells a new owner his empty system is finished.
         */
        totals.data !== undefined && totals.data.total > 0 ? (
          <EmptyState
            title="Nothing is waiting on you"
            description="Every open inquiry in this country has been dealt with."
          />
        ) : (
          <EmptyState
            title="No inquiries yet"
            description="This is where work that needs your attention appears. Create the first inquiry to get started."
          />
        )
      ) : (
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[600px] table-fixed border-collapse text-left">
            {/* Figma's measured column widths (nodes 12:3583-12:3592): CUSTOMER 168 ·
                STAGE 160 · DAYS 62 · OWNER 104 · NEXT STEP 106 = 600. `table-fixed`
                plus this colgroup is what keeps every row one line high, the way the
                design draws them, instead of letting the browser re-flow the columns
                and wrap "Collect advance" onto two. */}
            <colgroup>
              <col className="w-[168px]" />
              <col className="w-[160px]" />
              <col className="w-[62px]" />
              <col className="w-[104px]" />
              <col className="w-[106px]" />
            </colgroup>
            <thead>
              <tr className="text-th uppercase text-fg-muted">
                <th scope="col" className="h-8 border-b border-line px-3 font-medium">
                  Customer
                </th>
                <th scope="col" className="h-8 border-b border-line px-3 font-medium">
                  Stage
                </th>
                <th scope="col" className="h-8 border-b border-line px-3 font-medium">
                  Days
                </th>
                <th scope="col" className="h-8 border-b border-line px-3 font-medium">
                  Owner
                </th>
                <th scope="col" className="h-8 border-b border-line px-3 font-medium">
                  Next step
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const days = daysInStage(row.stage_entered_at);
                const nextStep = STAGES[row.stage].nextStep;
                return (
                  <tr
                    key={row.id}
                    className={index === rows.length - 1 ? '' : 'border-b border-line'}
                  >
                    <td className="h-10 px-3">
                      <Link
                        to={`/countries/${countryCode}/inquiries/${row.id}`}
                        // Figma's CUSTOMER cell is 168px and one line: a long party
                        // name ellipsises rather than pushing the row to two lines.
                        className="block truncate text-body13 font-medium text-fg hover:text-indigo"
                        title={row.subject ?? row.doc_no}
                      >
                        {/* The party name is projected by the API; `doc_no` is the
                            honest fallback when a party row has no name. */}
                        {row.party_name ?? row.doc_no}
                      </Link>
                    </td>
                    <td className="h-10 px-3">
                      <StagePill stage={row.stage} />
                    </td>
                    <td className="h-10 px-3">
                      <DaysPill days={days} />
                    </td>
                    <td className="h-10 px-3">
                      {row.assigned_to_name === null ? (
                        <span className="text-body13 text-fg-muted">Unassigned</span>
                      ) : (
                        <span
                          className="flex items-center gap-1.5 whitespace-nowrap"
                          title={row.assigned_to_name}
                        >
                          {/* 2.5b: photo when present (`assigned_to` is the id the
                           * avatar route keys on); 404 stays initials by contract. */}
                          <UserAvatar
                            name={row.assigned_to_name}
                            userId={row.assigned_to}
                            decorative
                          />
                          <span className="truncate text-body13 text-fg">{row.assigned_to_name}</span>
                        </span>
                      )}
                    </td>
                    <td className="h-10 truncate px-3 text-body13 text-fg-secondary">{nextStep ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
